import { PaymentMethod, type SyncableTransaction } from "elephant-web/gql";

/**
 * `Card` / `Mobile money` have no member on the Kenya-era `PaymentMethod`
 * enum, so take-payment records them as `OTHER` and preserves the chosen
 * method in `notes` behind this prefix. The encode (take-payment) and the
 * decode (`paymentMethodLabel`) live together so the pair cannot drift.
 *
 * The V2 waive and exempt flows also write `notes`, unprefixed — hence the
 * prefix check rather than reading `notes` wholesale.
 */
export const METHOD_NOTE_PREFIX = "METHOD: ";

/**
 * Total by design: `Record`, not `Partial<Record>`, so codegen adding a
 * `PaymentMethod` member fails the build here instead of silently rendering
 * a payment with no method.
 */
const ENUM_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.Cash]: "Cash",
  [PaymentMethod.Mpesa]: "M-Pesa",
  [PaymentMethod.Other]: "Other",
  [PaymentMethod.Void]: "Void",
};

/**
 * Display label for how a payment was taken. `undefined` when the method is
 * unknown — transactions written before method capture carry neither a
 * prefixed note nor a `paymentMethod`, and callers must render those without
 * inventing one.
 */
export const paymentMethodLabel = (
  transaction: Pick<SyncableTransaction, "paymentMethod" | "notes">,
): string | undefined => {
  const notes = transaction.notes ?? "";
  if (notes.startsWith(METHOD_NOTE_PREFIX)) {
    return notes.slice(METHOD_NOTE_PREFIX.length).trim() || undefined;
  }
  return transaction.paymentMethod
    ? ENUM_LABELS[transaction.paymentMethod]
    : undefined;
};
